import {
  IQuoteItem,
  IQuoteRespSchema,
  QuoteAmendmentVersionEnum,
  QuoteTypeEnum,
} from '@monetize/types/app';
import { MFlex } from '../../../../components/Monetize';
import MText, { MTextProps } from '../../../../components/Monetize/MText';
import { hasQuotePreviousQty, isQuoteQtyChanged } from '../../../../utils';

const getQuoteColors = (
  quote: IQuoteRespSchema | null,
  isAmendmentOrScheduled: boolean = false,
) => {
  if (
    (quote?.type === QuoteTypeEnum.AMENDMENT &&
      quote?.amendmentVersion === QuoteAmendmentVersionEnum.v1) ||
    quote?.type === QuoteTypeEnum.RENEWAL ||
    isAmendmentOrScheduled
  ) {
    return { color: '#8C32FF', bgcolor: '#F7ECFF' };
  }
  return { color: 'tIndigo.base', bgcolor: 'white' };
};

const getTextColor = ({
  color,
  isRemoved = false,
  hasChangedQuantity = false,
}: {
  color: string;
  isRemoved?: boolean;
  hasChangedQuantity?: boolean;
}) => {
  if (isRemoved) {
    return 'tGray.darkGrayPurple';
  }

  if (hasChangedQuantity) {
    return color;
  }

  return 'tPurple.base';
};

interface QuantityTextProps {
  value: string | number;
  item: IQuoteItem;
  quote: IQuoteRespSchema | null;
  type: QuoteTypeEnum;
  isScheduledChange: boolean;
  scheduledChangePriorQty?: number;
  styles?: MTextProps;
  isRemoved?: boolean;
  hideDifference?: boolean;
  isNonProratedProduct?: boolean;
}

export const QuantityText = ({
  value,
  item,
  quote,
  type,
  isScheduledChange,
  scheduledChangePriorQty,
  isRemoved,
  styles = {},
  hideDifference,
  isNonProratedProduct,
}: QuantityTextProps) => {
  const isAmendmentOrScheduled =
    type === QuoteTypeEnum.AMENDMENT || isScheduledChange;
  const { color } = getQuoteColors(quote, isAmendmentOrScheduled);
  const checkPrevQty = hasQuotePreviousQty(item, scheduledChangePriorQty);
  const checkQtyChanged = isQuoteQtyChanged(
    item,
    Number(value),
    isScheduledChange ? scheduledChangePriorQty : item.previousQuantity!,
  );

  const previousQuantity = isScheduledChange
    ? scheduledChangePriorQty
    : item.previousQuantity;
  const prevAsNum = checkPrevQty ? Number(previousQuantity) : 0;
  const valueAsNum = Number(value);
  const quantityDifference = `(${valueAsNum > prevAsNum ? '+' : '-'}${Math.abs(
    valueAsNum - prevAsNum,
  )})`;

  const hasChangedQuantity =
    checkPrevQty &&
    checkQtyChanged &&
    isAmendmentOrScheduled &&
    !hideDifference &&
    !isNonProratedProduct;

  const textColor = getTextColor({
    color,
    hasChangedQuantity,
    isRemoved,
  });

  return (
    <MFlex
      data-testid="quantity-cell"
      justifyContent="start"
      /**
       * Line up with the editable controls in this column, whose text starts
       * 11px in (1px border + the Input theme's 10px `sm` padding). Rows swap
       * between this and an input on hover, so a mismatch reads as a jump.
       */
      ps="11px"
      fontWeight={hasChangedQuantity ? '600' : 'unset'}
    >
      <MText color={textColor} data-testid={`${item.id}-quantity`} {...styles}>
        {value}
      </MText>
      {hasChangedQuantity && (
        <MText ml={1} lineHeight={1.4} {...styles}>
          {quantityDifference}
        </MText>
      )}
    </MFlex>
  );
};
